import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

/**
 * Client-side paging for long lists. Resets to page 1 whenever the list
 * identity changes (a new filter or search), so a filter never lands on an
 * empty page 7.
 */
export function usePagination<T>(items: readonly T[], pageSize = 15) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => setPage(1), [items]);
  const current = Math.min(page, pageCount);

  const pageItems = useMemo(
    () => items.slice((current - 1) * pageSize, current * pageSize),
    [items, current, pageSize]
  );

  return { page: current, pageCount, pageItems, setPage, total: items.length, pageSize };
}

export function Pagination({
  page,
  pageCount,
  total,
  pageSize,
  onPage,
  className,
}: {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
  className?: string;
}) {
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const btn =
    'flex h-7 w-7 items-center justify-center rounded-md border border-border-light text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:hover:bg-transparent';
  return (
    <div className={clsx('flex items-center justify-between gap-3 pt-3 text-body-xs text-text-tertiary', className)}>
      <span className="tabular-nums">
        {from}–{to} of {total.toLocaleString('en-GB')}
      </span>
      <div className="flex items-center gap-2">
        <button type="button" className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="tabular-nums">
          {page} / {pageCount}
        </span>
        <button
          type="button"
          className={btn}
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
